import React from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Lock, Unlock, Package,
  AlertTriangle, FileDown, Upload
} from 'lucide-react';

import { categoryConfig, getCategoryConfig } from './constants';
import { useInventoryData } from './hooks/useInventoryData';
import InventoryTable from './InventoryTable';
import GateToGraveSection from './GateToGraveSection';

export default function Phase2Inventory({ project, onUpdate, isLocked }) {
  const {
    loading,
    inventory,
    datasets,
    activeTab, setActiveTab,
    locking,
    unlocking,
    uploading,
    editingRowId, setEditingRowId,
    editingData, setEditingData,
    newRow, setNewRow,
    fileInputRef,
    g2gSettings,
    g2gSummary,
    showG2gSection, setShowG2gSection,
    savingG2gSettings,
    productLifetime, setProductLifetime,
    lifetimeGeography, setLifetimeGeography,
    isCradleToGrave,
    filteredInventory,
    counts,
    addNewRow,
    cancelNewRow,
    saveNewRow,
    startEditing,
    cancelEditing,
    saveEditing,
    deleteItem,
    saveG2gSettings,
    lockPhase,
    handleUnlock,
    downloadTemplate,
    handleFileUpload,
    handleDatasetSelect
  } = useInventoryData(project, onUpdate);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  const config = getCategoryConfig(activeTab);
  const Icon = config.icon;

  return (
    <div className="space-y-6" data-testid="phase2-inventory">
      {/* Phase Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-xl ${isLocked ? 'bg-emerald-100' : 'bg-sky-100'}`}>
            {isLocked ? <Lock className="h-6 w-6 text-emerald-600" /> : <Package className="h-6 w-6 text-sky-600" />}
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">ISO Phase 2 – Inventory (LCI)</h2>
            <p className="text-slate-500">Quantify all material and energy flows from the dataset library</p>
          </div>
        </div>
        <Badge variant={isLocked ? "default" : "outline"} className={isLocked ? "bg-emerald-600" : "border-slate-300 text-slate-600"}>
          {isLocked ? 'Locked' : inventory.length > 0 ? 'In Progress' : 'Not Started'}
        </Badge>
      </div>

      {/* Dataset Warning */}
      {datasets.length === 0 && (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            No datasets found. Please add datasets to the Dataset Library first before adding inventory items.
          </AlertDescription>
        </Alert>
      )}

      {/* Category Tabs - Cradle to Gate */}
      <div className="flex gap-2 p-1.5 bg-slate-100 rounded-xl">
        {Object.entries(categoryConfig).map(([key, cfg]) => {
          const TabIcon = cfg.icon;
          const count = counts[key];
          const isActive = activeTab === key;

          return (
            <button
              key={key}
              onClick={() => { setActiveTab(key); setNewRow(null); setEditingRowId(null); }}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                isActive
                  ? `${cfg.color} border bg-white shadow-sm`
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
              data-testid={`tab-${key}`}
            >
              <TabIcon className="h-4 w-4" />
              <span className="hidden sm:inline">{cfg.label}</span>
              {count > 0 && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? 'bg-slate-200' : 'bg-slate-200'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Gate-to-Grave Section */}
      {isCradleToGrave && (
        <GateToGraveSection
          isLocked={isLocked}
          showG2gSection={showG2gSection}
          setShowG2gSection={setShowG2gSection}
          g2gSummary={g2gSummary}
          g2gSettings={g2gSettings}
          counts={counts}
          productLifetime={productLifetime}
          setProductLifetime={setProductLifetime}
          lifetimeGeography={lifetimeGeography}
          setLifetimeGeography={setLifetimeGeography}
          savingG2gSettings={savingG2gSettings}
          onSaveG2gSettings={saveG2gSettings}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setNewRow={setNewRow}
          setEditingRowId={setEditingRowId}
        />
      )}

      {/* Active Category Content */}
      <Card className={`border ${config.bgColor} shadow-sm`}>
        <CardHeader className="pb-4 bg-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${config.color}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg text-slate-900">{config.label}</CardTitle>
                <p className="text-sm text-slate-500">{filteredInventory.length} items • Select from dataset library</p>
              </div>
            </div>

            {!isLocked && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => downloadTemplate(activeTab)} data-testid="download-template-btn" className="border-slate-300">
                  <FileDown className="h-4 w-4 mr-2" />
                  Template
                </Button>

                <input type="file" ref={fileInputRef} accept=".csv" onChange={handleFileUpload} className="hidden" />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-csv-btn" className="border-slate-300">
                  {uploading ? <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <Upload className="h-4 w-4 mr-2" />}
                  {uploading ? 'Uploading...' : 'Upload CSV'}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="bg-white rounded-b-lg overflow-visible">
          <InventoryTable
            config={config}
            filteredInventory={filteredInventory}
            isLocked={isLocked}
            editingRowId={editingRowId}
            editingData={editingData}
            setEditingData={setEditingData}
            newRow={newRow}
            setNewRow={setNewRow}
            datasets={datasets}
            activeTab={activeTab}
            onStartEditing={startEditing}
            onCancelEditing={cancelEditing}
            onSaveEditing={saveEditing}
            onDeleteItem={deleteItem}
            onSaveNewRow={saveNewRow}
            onCancelNewRow={cancelNewRow}
            onAddNewRow={addNewRow}
            handleDatasetSelect={handleDatasetSelect}
          />
        </CardContent>
      </Card>

      {/* Lock Phase Button */}
      {!isLocked && inventory.length > 0 && (
        <div className="flex justify-end">
          <Button
            onClick={lockPhase}
            disabled={locking}
            className="bg-sky-600 hover:bg-sky-700 text-white"
            data-testid="lock-phase-btn"
          >
            {locking ? (
              <>
                <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Locking...
              </>
            ) : (
              <>
                <Lock className="h-4 w-4 mr-2" />
                Lock Phase 2 & Proceed to LCIA
              </>
            )}
          </Button>
        </div>
      )}

      {/* Unlock Button when locked */}
      {isLocked && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={handleUnlock}
            disabled={unlocking}
            className="border-amber-500 text-amber-600 hover:bg-amber-50"
            data-testid="unlock-phase2-btn"
          >
            {unlocking ? (
              <>
                <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
                Unlocking...
              </>
            ) : (
              <>
                <Unlock className="h-4 w-4 mr-2" />
                Unlock & Edit Inventory
              </>
            )}
          </Button>
        </div>
      )}

      {/* Summary */}
      {inventory.length > 0 && (
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {Object.entries(categoryConfig).map(([key, cfg]) => {
                const count = counts[key];
                const CategoryIcon = cfg.icon;
                return (
                  <div key={key} className="p-3 bg-white rounded-lg border border-slate-200">
                    <CategoryIcon className={`h-5 w-5 mx-auto mb-1 ${cfg.color.split(' ')[1]}`} />
                    <div className="text-2xl font-bold text-slate-900">{count}</div>
                    <div className="text-xs text-slate-500">{cfg.label}</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
